import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import RegistrationInteractionEvent, {
  type RegistrationMethod,
  type RegistrationPlacement
} from '~/events/RegistrationInteractionEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { log } from '~/utils/log';

export type RegistrationEntry = {
  placement: RegistrationPlacement;
  productId?: string;
};

const RegistrationEntryContext = createContext<RegistrationEntry | null>(null);

export function RegistrationEntryProvider({
  children,
  entry
}: {
  children: ReactNode;
  entry: RegistrationEntry;
}) {
  return (
    <RegistrationEntryContext.Provider value={entry}>
      {children}
    </RegistrationEntryContext.Provider>
  );
}

function useSendRegistration() {
  const { isBot } = useRootData();
  const user = useUser();

  return (entry: RegistrationEntry, method?: RegistrationMethod) => {
    if (user.isLoggedIn) return;

    return new RegistrationInteractionEvent({
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      method,
      placement: entry.placement,
      productId: entry.productId
    }).send();
  };
}

export function useRegistrationStart() {
  const send = useSendRegistration();

  return (entry: RegistrationEntry) => send(entry);
}

export function useRegistrationMethod() {
  const entry = useContext(RegistrationEntryContext);
  const send = useSendRegistration();

  return (method: RegistrationMethod) => {
    if (!entry) {
      log.error('Registration method picked outside a sign-in prompt', {
        method
      });

      return;
    }

    return send(entry, method);
  };
}
